import { useT } from "../i18n/hooks.ts";
import type { GenericLayer } from "../model/genericLayer.ts";

/**
 * Temporary legend for generic reference layers (genericLayer.ts) — name +
 * color swatch per layer, so a color is identifiable on the map without
 * having to open "Edit project" → "Layers" (GenericLayersPanel.tsx, the
 * only other place a layer's color is shown). Not built into the native
 * `LayersControl` itself: react-leaflet v4 types `Overlay`'s `name` prop as
 * a plain `string`, not a `ReactNode` — no swatch can be embedded directly
 * in that checkbox list. Only lists VISIBLE layers (`l.visible`): a hidden
 * layer isn't drawn, so its color doesn't need explaining either. Renders
 * nothing while there's nothing visible to explain — same "only exists
 * while relevant" reasoning as AssignmentColorLegend.tsx, which this
 * mirrors in spirit (name/swatch list) but not in position, to avoid
 * colliding with it when both happen to be shown at once (see styles.css).
 */
export function GenericLayersLegend({ layers }: { layers: GenericLayer[] }) {
  const t = useT();
  const visibleLayers = layers.filter((l) => l.visible);
  if (visibleLayers.length === 0) return null;
  return (
    <div className="generic-layers-legend">
      <strong>{t("panels.layers")}</strong>
      <ul>
        {visibleLayers.map((l) => (
          <li key={l.id}>
            <span
              className="generic-layers-legend-swatch"
              style={{ backgroundColor: l.style.color }}
            />
            {l.name}
          </li>
        ))}
      </ul>
    </div>
  );
}
